import "./SetUp.css";
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import {
  MailOutlined,
  SwapOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { AuthContext } from "../context/AuthContext";
import { auth, db, functions } from "../firebase";
import {
  FleetRoles,
  getFleetRoleLabel,
  isManagerRole,
  normalizeFleetRole,
} from "../auth/fleetRoles";
import { useGlobalToast } from "../context/ToastProvider";

function getMemberStatusLabel(status, t) {
  const normalized = String(status || "").trim().toUpperCase();
  const translationKeyByStatus = {
    ACTIVE: "account.statuses.active",
    PENDING: "account.statuses.pending",
    DISABLED: "account.statuses.disabled",
    ACCEPTED: "account.statuses.accepted",
    CANCELLED: "account.statuses.cancelled",
  };

  const translationKey = translationKeyByStatus[normalized];
  if (!translationKey) {
    return t("account.statuses.unknown", { defaultValue: normalized || "Unknown" });
  }

  return t(translationKey, { defaultValue: normalized });
}

export default function Account() {
  const [memberInvites, setMemberInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState(FleetRoles.OPERATIONS);
  const [inviteUrl, setInviteUrl] = useState("");
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [fleetNameInput, setFleetNameInput] = useState("");
  const [creatingMemberInvite, setCreatingMemberInvite] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [cancellingInviteId, setCancellingInviteId] = useState("");
  const [changingRoleUserId, setChangingRoleUserId] = useState("");
  const [removingMemberUserId, setRemovingMemberUserId] = useState("");
  const [transferTargetUserId, setTransferTargetUserId] = useState("");
  const [transferringOwnership, setTransferringOwnership] = useState(false);
  const [deletingFleet, setDeletingFleet] = useState(false);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { getFleetID, fleetData, fleetRole, user, logOutUser, authenticateUser } = useContext(AuthContext);
  const fleetId = getFleetID();
  const toast = useGlobalToast();
  const { t } = useTranslation();

  const canManageUsers = isManagerRole(fleetRole);
  const isOwner = fleetRole === FleetRoles.OWNER;
  const currentDisplayName = String(user?.displayName || user?.email || "").trim();
  const currentFleetName = String(fleetData?.name || "").trim();
  const trimmedDisplayName = displayNameInput.trim();
  const trimmedFleetName = fleetNameInput.trim();

  const memberRoleOptions = useMemo(
    () =>
      [
        FleetRoles.EXECUTIVE,
        FleetRoles.OPERATIONS,
        FleetRoles.MAINTENANCE,
        FleetRoles.HHRR,
      ].map((role) => ({
        value: role,
        label: getFleetRoleLabel(role, t),
      })),
    [t]
  );

  useEffect(() => {
    setDisplayNameInput(currentDisplayName);
  }, [currentDisplayName]);

  useEffect(() => {
    setFleetNameInput(currentFleetName);
  }, [currentFleetName]);

  useEffect(() => {
    if (!fleetId || !canManageUsers) {
      setMemberInvites([]);
      setMembers([]);
      return undefined;
    }

    const invitesRef = collection(db, "fleets", fleetId, "member_invites");
    const membersRef = collection(db, "fleets", fleetId, "members");

    const unsubscribeInvites = onSnapshot(
      invitesRef,
      (snapshot) => {
        const docs = snapshot.docs
          .map((row) => ({ id: row.id, ...row.data() }))
          .filter(
            (invite) =>
              Boolean(normalizeFleetRole(invite.role))
              && String(invite.status || "").toUpperCase() === "PENDING"
          )
          .sort((a, b) => {
            const aMillis = a.createdAt?.toMillis?.() || 0;
            const bMillis = b.createdAt?.toMillis?.() || 0;
            return bMillis - aMillis;
          });
        setMemberInvites(docs);
      },
      (error) => {
        console.error("Error fetching member invites:", error);
      }
    );

    const unsubscribeMembers = onSnapshot(
      membersRef,
      (snapshot) => {
        const docs = snapshot.docs
          .map((row) => ({ id: row.id, ...row.data() }))
          .filter((member) => Boolean(normalizeFleetRole(member.role)))
          .sort((a, b) =>
            String(a.displayName || a.email || a.id).localeCompare(
              String(b.displayName || b.email || b.id)
            )
          );
        setMembers(docs);
      },
      (error) => {
        console.error("Error fetching members:", error);
      }
    );

    return () => {
      unsubscribeInvites();
      unsubscribeMembers();
    };
  }, [fleetId, canManageUsers]);

  useEffect(() => {
    if (!transferTargetUserId) return;

    const transferTargetStillAvailable = members.some(
      (member) =>
        member.userId === transferTargetUserId
        && String(member.role || "").toUpperCase() === FleetRoles.EXECUTIVE
        && String(member.status || "").toUpperCase() === "ACTIVE"
    );

    if (!transferTargetStillAvailable) {
      setTransferTargetUserId("");
    }
  }, [members, transferTargetUserId]);

  const profileDirty =
    trimmedDisplayName !== currentDisplayName || (isOwner && trimmedFleetName !== currentFleetName);

  const createMemberInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setCreatingMemberInvite(true);
      setInviteUrl("");
      const fn = httpsCallable(functions, "create_member_invite");
      const response = await fn({
        fleetId,
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
      });
      const generatedUrl = response?.data?.data?.inviteUrl || "";
      const emailDelivery = response?.data?.data?.emailDelivery || {};
      setInviteUrl(generatedUrl);
      toast({
        title: emailDelivery.sent
          ? t("account.createInviteEmailSent")
          : t("account.createInviteLinkOnly"),
        status: emailDelivery.sent ? "success" : "warning",
      });
    } catch (error) {
      console.error("Error creating member invite:", error);
      toast({ error,
        title: t("account.createInviteError"),
        status: "error",
      });
    } finally {
      setCreatingMemberInvite(false);
    }
  };

  const handleCopyInviteUrl = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast({
        title: t("account.copyInviteSuccess"),
        status: "success",
      });
    } catch (error) {
      console.error("Failed copying invite URL:", error);
      toast({ title: t("feedback.copyFailed"), status: "warning" });
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      setCancellingInviteId(inviteId);
      const fn = httpsCallable(functions, "cancel_member_invite");
      await fn({ fleetId, inviteId });
      toast({
        title: t("account.cancelInviteSuccess"),
        status: "success",
      });
    } catch (error) {
      console.error("Error cancelling member invite:", error);
      toast({ error,
        title: t("account.cancelInviteError"),
        status: "error",
      });
    } finally {
      setCancellingInviteId("");
    }
  };

  const handleSaveProfile = async () => {
    if (!trimmedDisplayName) {
      toast({
        title: t("account.profileNameRequired"),
        status: "error",
      });
      return;
    }

    if (isOwner && !trimmedFleetName) {
      toast({
        title: t("account.fleetNameRequired"),
        status: "error",
      });
      return;
    }

    try {
      setSavingProfile(true);
      const fn = httpsCallable(functions, "update_member_profile");
      await fn({
        fleetId,
        displayName: trimmedDisplayName,
        fleetName: isOwner ? trimmedFleetName : undefined,
      });
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      await authenticateUser({ forceRefresh: true });
      toast({
        title: t("account.profileUpdated"),
        status: "success",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({ error,
        title: t("account.profileUpdateError"),
        status: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleMemberRoleChange = async (memberUserId, role) => {
    try {
      setChangingRoleUserId(memberUserId);
      const fn = httpsCallable(functions, "update_member_role");
      await fn({ fleetId, memberUserId, role });
      toast({
        title: t("account.memberRoleUpdated"),
        status: "success",
      });
    } catch (error) {
      console.error("Error updating member role:", error);
      toast({ error,
        title: t("account.memberRoleUpdateError"),
        status: "error",
      });
    } finally {
      setChangingRoleUserId("");
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    try {
      setRemovingMemberUserId(memberUserId);
      const fn = httpsCallable(functions, "remove_member");
      await fn({ fleetId, memberUserId });
      toast({
        title: t("account.memberRemoved"),
        status: "success",
      });
    } catch (error) {
      console.error("Error removing member:", error);
      toast({ error,
        title: t("account.memberRemoveError"),
        status: "error",
      });
    } finally {
      setRemovingMemberUserId("");
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferTargetUserId) return;
    try {
      setTransferringOwnership(true);
      const fn = httpsCallable(functions, "transfer_fleet_ownership");
      await fn({
        fleetId,
        targetUserId: transferTargetUserId,
        confirmed: true,
      });
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }
      await authenticateUser({ forceRefresh: true });
      toast({
        title: t("account.transferOwnershipSuccess"),
        status: "success",
      });
    } catch (error) {
      console.error("Error transferring ownership:", error);
      toast({ error,
        title: t("account.transferOwnershipError"),
        status: "error",
      });
    } finally {
      setTransferringOwnership(false);
    }
  };

  const handleDeleteFleetAccount = async () => {
    if (!isOwner || deleteConfirmValue !== "DELETE") return;
    try {
      setDeletingFleet(true);
      const fn = httpsCallable(functions, "delete_fleet_account");
      await fn({ fleetId, confirmed: true });
      await logOutUser();
    } catch (error) {
      console.error("Error deleting fleet account:", error);
      toast({ error,
        title: t("account.deleteFleetError"),
        status: "error",
      });
    } finally {
      setDeletingFleet(false);
      setDeleteModalOpen(false);
      setDeleteConfirmValue("");
    }
  };

  if (!fleetId) {
    return (
      <div className="set-box">
        <Card className="card-container" style={{ maxWidth: 980, margin: "0 auto" }}>
          <Typography.Title level={5} style={{ textAlign: "center" }}>
            {t("account.loading", { defaultValue: "Loading account..." })}
          </Typography.Title>
        </Card>
      </div>
    );
  }

  const transferCandidates = members
    .filter(
      (member) =>
        String(member.role || "").toUpperCase() === FleetRoles.EXECUTIVE
        && String(member.status || "").toUpperCase() === "ACTIVE"
    )
    .map((member) => ({
      label: member.displayName || member.email || member.userId,
      value: member.userId,
    }));

  return (
    <div className="setup-page">
      <div className="setup-page-inner">
        <Card className="setup-hero" bordered={false}>
          <Space direction="vertical" size={4}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t("navbar.account", { defaultValue: "Account" })}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t("account.subtitle", {
                defaultValue:
                  "Manage team access, ownership, and account-level controls from one place.",
              })}
            </Typography.Text>
          </Space>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={canManageUsers ? 12 : 16}>
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>{t("account.profileTitle")}</span>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                <Typography.Text type="secondary">
                  {t("account.profileHelp")}
                </Typography.Text>
                <div>
                  <Typography.Text strong>{t("account.displayNameLabel")}</Typography.Text>
                  <Input
                    value={displayNameInput}
                    onChange={(event) => setDisplayNameInput(event.target.value)}
                    placeholder={t("account.displayNamePlaceholder")}
                    style={{ marginTop: 8 }}
                  />
                </div>
                {isOwner ? (
                  <div>
                    <Typography.Text strong>{t("account.fleetNameLabel")}</Typography.Text>
                    <Input
                      value={fleetNameInput}
                      onChange={(event) => setFleetNameInput(event.target.value)}
                      placeholder={t("account.fleetNamePlaceholder")}
                      style={{ marginTop: 8 }}
                    />
                  </div>
                ) : null}
                <Button
                  type="primary"
                  onClick={handleSaveProfile}
                  loading={savingProfile}
                  disabled={!profileDirty || !trimmedDisplayName || (isOwner && !trimmedFleetName)}
                >
                  {t("account.saveProfileButton")}
                </Button>
              </Space>
            </Card>
          </Col>

          {canManageUsers ? (
            <Col xs={24} xl={12}>
              <Card
                title={
                  <Space>
                    <TeamOutlined />
                    <span>{t("account.teamManagementTitle")}</span>
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                  <Typography.Text type="secondary">
                    {t("account.teamManagementHelp")}
                  </Typography.Text>
                  <Space wrap style={{ width: "100%" }}>
                    <Input
                      style={{ minWidth: 260 }}
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder={t("account.inviteEmailPlaceholder")}
                    />
                    <Select
                      value={inviteRole}
                      onChange={setInviteRole}
                      options={memberRoleOptions}
                      style={{ width: 190 }}
                    />
                    <Button
                      type="primary"
                      onClick={createMemberInvite}
                      loading={creatingMemberInvite}
                      disabled={!inviteEmail.trim()}
                    >
                      {t("account.createInviteButton")}
                    </Button>
                  </Space>
                  {inviteUrl ? (
                    <Space style={{ width: "100%" }}>
                      <Input readOnly value={inviteUrl} />
                      <Button onClick={handleCopyInviteUrl}>{t("account.copyInviteButton")}</Button>
                    </Space>
                  ) : null}
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography.Text strong>{t("account.membersTitle")}</Typography.Text>
                  <List
                    dataSource={members}
                    locale={{ emptyText: t("account.noMembers") }}
                    renderItem={(member) => {
                      const memberUserId = member.userId || member.id;
                      const canEdit = memberUserId !== user?.uid && memberUserId !== fleetId;
                      const email = member.email || t("account.noEmail");
                      const roleLabel = getFleetRoleLabel(member.role, t) || t("account.unknownRole");
                      const statusLabel = getMemberStatusLabel(member.status, t);

                      return (
                        <List.Item
                          actions={[
                            <Select
                              key={`role-${member.id}`}
                              size="small"
                              value={member.role}
                              disabled={!canEdit}
                              loading={changingRoleUserId === memberUserId}
                              onChange={(value) => handleMemberRoleChange(memberUserId, value)}
                              options={memberRoleOptions}
                              style={{ width: 170 }}
                            />,
                            <Popconfirm
                              key={`remove-${member.id}`}
                              title={t("account.removeMemberConfirm")}
                              onConfirm={() => handleRemoveMember(memberUserId)}
                              okText={t("account.removeMemberButton")}
                              cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
                              disabled={!canEdit}
                            >
                              <Button
                                danger
                                size="small"
                                disabled={!canEdit}
                                loading={removingMemberUserId === memberUserId}
                              >
                                {t("account.removeMemberButton")}
                              </Button>
                            </Popconfirm>,
                          ]}
                        >
                          <List.Item.Meta
                            title={member.displayName || member.email || member.userId}
                            description={t("account.memberMeta", {
                              email,
                              role: roleLabel,
                              status: statusLabel,
                            })}
                          />
                        </List.Item>
                      );
                    }}
                  />
                </Space>
              </Card>
            </Col>
          ) : null}
        </Row>

        {canManageUsers ? (
          <Row gutter={[16, 16]} style={{ marginTop: 4 }}>
            <Col xs={24} xl={12}>
              <Card
                title={
                  <Space>
                    <MailOutlined />
                    <span>{t("account.pendingInvitesTitle")}</span>
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                  <Typography.Text type="secondary">
                    {t("account.pendingInvitesHelp")}
                  </Typography.Text>
                  <List
                    dataSource={memberInvites}
                    locale={{ emptyText: t("account.noPendingInvites") }}
                    renderItem={(invite) => (
                      <List.Item
                        actions={[
                          <Popconfirm
                            key={`cancel-${invite.id}`}
                            title={t("account.cancelInviteConfirm")}
                            onConfirm={() => handleCancelInvite(invite.id)}
                            okText={t("account.cancelInviteButton")}
                            cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
                          >
                            <Button
                              danger
                              size="small"
                              loading={cancellingInviteId === invite.id}
                            >
                              {t("account.cancelInviteButton")}
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={`${invite.email || t("account.noEmail")} • ${
                            getFleetRoleLabel(invite.role, t) || t("account.unknownRole")
                          }`}
                          description={t("account.pendingInviteMeta", {
                            status: getMemberStatusLabel(invite.status, t),
                          })}
                        />
                      </List.Item>
                    )}
                  />
                </Space>
              </Card>
            </Col>
          </Row>
        ) : null}

        {isOwner ? (
          <Row gutter={[16, 16]} style={{ marginTop: 4 }}>
            <Col xs={24} xl={12}>
              <Card
                title={
                  <Space>
                    <SwapOutlined />
                    <span>{t("account.transferOwnershipTitle")}</span>
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }} size={12}>
                  <Typography.Text type="secondary">
                    {t("account.transferOwnershipHelp")}
                  </Typography.Text>
                  <Select
                    value={transferTargetUserId || undefined}
                    onChange={setTransferTargetUserId}
                    placeholder={t("account.transferOwnershipPlaceholder")}
                    options={transferCandidates}
                    disabled={transferCandidates.length === 0}
                  />
                  {transferCandidates.length === 0 ? (
                    <Typography.Text type="secondary">
                      {t("account.noEligibleExecutives")}
                    </Typography.Text>
                  ) : null}
                  <Popconfirm
                    title={t("account.transferOwnershipConfirmTitle")}
                    description={t("account.transferOwnershipConfirmDescription")}
                    onConfirm={handleTransferOwnership}
                    okText={t("account.transferOwnershipButton")}
                    cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
                    disabled={!transferTargetUserId}
                  >
                    <Button
                      type="primary"
                      danger
                      disabled={!transferTargetUserId}
                      loading={transferringOwnership}
                    >
                      {t("account.transferOwnershipButton")}
                    </Button>
                  </Popconfirm>
                </Space>
              </Card>
            </Col>
            <Col xs={24} xl={12}>
              <Card
                title={
                  <Space>
                    <WarningOutlined />
                    <span>{t("account.dangerZoneTitle")}</span>
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Typography.Text type="secondary">
                    {t("account.dangerZoneHelp")}
                  </Typography.Text>
                  <Button danger onClick={() => setDeleteModalOpen(true)}>
                    {t("account.deleteAccountButton")}
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        ) : null}
      </div>

      <Modal
        open={deleteModalOpen}
        title={t("account.deleteFleetModalTitle")}
        okText={t("account.deleteAccountButton")}
        okButtonProps={{ danger: true, disabled: deleteConfirmValue !== "DELETE", loading: deletingFleet }}
        cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
        onOk={handleDeleteFleetAccount}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteConfirmValue("");
        }}
      >
        <Typography.Paragraph>
          {t("account.deleteFleetModalDescription", { keyword: "DELETE" })}
        </Typography.Paragraph>
        <Input value={deleteConfirmValue} onChange={(event) => setDeleteConfirmValue(event.target.value)} />
      </Modal>
    </div>
  );
}
